import React, { useCallback, useMemo, useState } from 'react';
import { FiDownload, FiArrowRight } from 'react-icons/fi';
import UserStatusBadge from './UserStatusBadge';
import UserStatusActions from './UserStatusActions';

const downloadPanCard = async (url, fileName) => {
  try {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = fileName || 'PAN-card.jpg';
    a.click();
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(url, '_blank');
  }
};

const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : '—');
const formatDateTime = (date) => (date ? new Date(date).toLocaleString() : '—');

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 py-2.5">
    <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
    <span className="text-sm font-semibold text-slate-800">{value || '—'}</span>
  </div>
);

/** Section with title only — no card, used inside the main panel */
const Section = ({ title, children }) => (
  <div className="pb-8 last:pb-0">
    <h4 className="text-base font-semibold text-slate-800 mb-4">{title}</h4>
    {children}
  </div>
);

/**
 * Read-only view of user profile, content types, contract & payment, and bank details.
 * Layout: Left = Profile card + Instagram overview. Right = Profile info + Engagement (row 1), Content types + Contract (row 2).
 */
const UserDetailsView = ({
  user,
  onStatusChange,
  showAccountActions = true,
}) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const handleCloseModal = useCallback(() => {
    setSelectedImage(null);
  }, []);

  const instagramFollowers = user?.instagramFollowers ?? 0;
  const instagramFollowing = user?.instagramFollowing ?? 0;
  const instagramMediaCount = user?.instagramMediaCount ?? 0;
  const isProfileComplete = user?.isProfileComplete ? 'Yes' : 'No';

  const instagramStats = useMemo(
    () => [
      { label: 'Followers', value: instagramFollowers },
      { label: 'Following', value: instagramFollowing },
      { label: 'Media count', value: instagramMediaCount },
      { label: 'Profile complete', value: isProfileComplete },
    ],
    [instagramFollowers, instagramFollowing, instagramMediaCount, isProfileComplete]
  );

  if (!user) return null;

  const hasBankDetails =
    user.bankDetails &&
    typeof user.bankDetails === 'object' &&
    (user.bankDetails.name || user.bankDetails.bankName);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Image Modal (same interaction as InfluencerProfile) */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={handleCloseModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative rounded-xl max-w-2xl w-full aspect-[4/3] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute -top-4 -right-4 px-3 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-300 transition-all z-10 shadow"
            >
              Close
            </button>
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={selectedImage}
                alt="Profile"
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* Left: Single card — Profile + Instagram overview */}
      <div className="lg:w-80 shrink-0 flex flex-col gap-6">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="h-20 bg-gradient-to-br from-slate-700 to-slate-900" />
          <div className="relative px-5 pb-5 pt-0">
            <div className="-mt-9 flex justify-center">
              {user.profileImage?.url ? (
                <img
                  src={user.profileImage.url}
                  alt={user.username || 'User'}
                  className="h-[72px] w-[72px] rounded-xl object-cover ring-4 ring-white shadow-md cursor-pointer"
                  onClick={() => setSelectedImage(user.profileImage.url)}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className={`rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white text-xl font-bold ring-4 ring-white shadow-md h-[72px] w-[72px] ${user.profileImage?.url ? 'hidden' : ''}`}
              >
                {user.username?.charAt(0).toUpperCase() || 'U'}
              </div>
            </div>
            <div className="text-center mt-4">
              <h3 className="text-lg font-bold text-slate-800">{user.username}</h3>
              <p className="text-sm text-slate-500 mt-1 truncate max-w-full" title={user.email}>
                {user.email || 'No email'}
              </p>
              <div className="mt-3 flex justify-center">
                <UserStatusBadge status={user.status} />
              </div>
            </div>
            {showAccountActions && onStatusChange && (
              <div className="mt-5 pt-4 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  Account actions
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <UserStatusActions user={user} onStatusChange={onStatusChange} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Instagram overview — below profile, same column, no extra card */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h4 className="text-base font-semibold text-slate-800">Instagram overview</h4>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-3">
              {instagramStats.map(({ label, value }) => (
                <div key={label} className="rounded-lg bg-slate-50 p-3 text-center">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {label}
                  </p>
                  <p className="text-lg font-bold text-slate-800 mt-0.5 tabular-nums">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right: One panel — Profile info + Engagement (row 1), Content types + Contract (row 2) */}
      <div className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-white shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
          {/* Row 1: Profile information | Engagement averages */}
          <Section title="Profile information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <DetailRow label="Mobile" value={user.mobile} />
              <DetailRow label="Gender" value={user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : '—'} />
              <DetailRow label="Account type" value={user.accountType} />
              <DetailRow label="Date of birth" value={formatDate(user.dob)} />
              <DetailRow label="Country" value={user.country} />
              <DetailRow label="State" value={user.state} />
              <DetailRow label="City" value={user.city} />
              <DetailRow label="Postal code" value={user.postalCode} />
              <DetailRow label="Last login" value={formatDateTime(user.lastLogin)} />
            </div>
          </Section>

          <Section title="Engagement averages">
            {user.instagramAverages ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                    <h5 className="text-sm font-semibold text-slate-700 mb-3">Posts</h5>
                    <div className="space-y-2 text-sm">
                      {['Reach', 'Likes', 'Comments', 'Shares', 'Saves', 'Views'].map((key) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-slate-500">{key}</span>
                          <span className="font-semibold text-slate-800 tabular-nums">
                            {user.instagramAverages.posts?.[key.toLowerCase()] ?? 0}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between pt-2 font-medium text-slate-700 border-t border-slate-200">
                        <span>Total</span>
                        <span className="tabular-nums">
                          {user.instagramAverages.posts?.total_interactions ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                    <h5 className="text-sm font-semibold text-slate-700 mb-3">Reels</h5>
                    <div className="space-y-2 text-sm">
                      {['Reach', 'Likes', 'Comments', 'Shares', 'Saves', 'Views'].map((key) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-slate-500">{key}</span>
                          <span className="font-semibold text-slate-800 tabular-nums">
                            {user.instagramAverages.reels?.[key.toLowerCase()] ?? 0}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between pt-2 font-medium text-slate-700 border-t border-slate-200">
                        <span>Total</span>
                        <span className="tabular-nums">
                          {user.instagramAverages.reels?.total_interactions ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Last synced: {formatDateTime(user.instagramAverages.lastSynced)}
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-500">No engagement data</p>
            )}
          </Section>

          {/* Row 2: Content types | Contract & payment */}
          <div className="space-y-6">
            <Section title="Content types">
              {user.contentType && user.contentType.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {user.contentType.map((type, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-indigo-50 text-indigo-700"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No content types specified</p>
              )}
            </Section>

            {/* Keep PAN below content types to balance both columns */}
            {(user.bankDetails?.panDetails || user.bankDetails?.panCardImage?.url) && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                <h5 className="text-sm font-semibold text-slate-700 mb-2">PAN</h5>
                {user.bankDetails?.panDetails && (
                  <p className="text-sm font-semibold text-slate-800 mb-3">
                    {user.bankDetails.panDetails}
                  </p>
                )}
                {user.bankDetails?.panCardImage?.url && (
                  <>
                    <div className="rounded-lg border border-slate-200 bg-white p-3 inline-block shadow-sm">
                      <img
                        src={user.bankDetails.panCardImage.url}
                        alt="PAN card"
                        className="max-h-52 object-contain"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        downloadPanCard(
                          user.bankDetails.panCardImage.url,
                          `PAN-card-${(user.username || user.bankDetails?.name || 'user').replace(/\s+/g, '-')}.jpg`
                        )
                      }
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-700 font-medium text-sm hover:bg-slate-50 hover:border-slate-400 transition-colors"
                    >
                      <FiDownload className="w-4 h-4" />
                      Download PAN card
                      <FiArrowRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <Section title="Contract & payment">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <DetailRow label="Contract status" value={user.contractStatus} />
              <DetailRow label="Bank details" value={user.bankDetails ? 'Added' : 'Not added'} />
            </div>
            {hasBankDetails && (
              <div className="mt-4 space-y-5">
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
                  <h5 className="text-sm font-semibold text-slate-700 mb-3">Bank account</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0">
                    <DetailRow label="Account holder" value={user.bankDetails.name} />
                    <DetailRow label="Account number" value={user.bankDetails.bankAccount} />
                    <DetailRow label="IFSC" value={user.bankDetails.ifscCode} />
                    <DetailRow label="Bank name" value={user.bankDetails.bankName} />
                    <DetailRow label="Account type" value={user.bankDetails.accountType} />
                    <DetailRow label="Branch" value={user.bankDetails.bankBranch} />
                  </div>
                </div>
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsView;
